import React, { useState, useCallback } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useMutation, gql } from "@apollo/client";
import { useDropzone } from "react-dropzone";
import { ScanPreview } from "./PreviewScan";
import { SearchParticipants } from "@/pages/participantDocuments/search";
import { useUserPermissions } from "@/store/useUserPermissions";
import { hasAnyPolicy } from "@/utils/policies";

const UPDATE_PARTICIPANT = gql`
  mutation UpdateParticipant(
    $programNumber: Int!
    $input: participantProgramDataInput!
  ) {
    updateParticipantProgramDataByProgramNumber(
      programNumber: $programNumber
      input: $input
    ) {
      responseCode
      message
    }
  }
`;

const TRANSFER_PARTICIPANT_SCANS = gql`
  mutation TransferParticipantScans($odrnumber: Int!, $scanID: [String]) {
    transferParticipantScans(odrnumber: $odrnumber, scanID: $scanID)
  }
`;

const modalStyles = {
  content: {
    top: '10%',
    left: '10%',
    right: '10%',
    bottom: '10%',
    padding: 0,
    border: 'none',
    background: 'transparent'
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  }
};

export const EditScans = ({
  isOpen,
  onClose,
  programNumber,
  odrNumber,
  scans,
}: {
  isOpen: boolean;
  onClose: () => void;
  programNumber: number;
  odrNumber: number;
  scans: any[];
}) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadType, setUploadType] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadLabel, setUploadLabel] = useState("");
  const [selectedScan, setSelectedScan] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [selectedScanIds, setSelectedScanIds] = useState<string[]>([]);
  const [transferScanModal, setTransferScanModal] = useState(false);

  const [updateParticipant] = useMutation(UPDATE_PARTICIPANT);
  const [transferScans] = useMutation(TRANSFER_PARTICIPANT_SCANS);
  const { policies } = useUserPermissions();
  const canTransferScans = hasAnyPolicy(policies, [
    "*:*~*",
    "admin:access~full",
    "tools:billing:access~full",
  ]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setUploadFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) return;

    try {
      const result = await updateParticipant({
        variables: {
          programNumber,
          input: {
            scans: [
              {
                filetype: uploadType,
                filelabel: uploadLabel,
                filedata: await fileToBase64(uploadFile),
                filename: uploadFile.name,
              },
            ],
          },
        },
      });

      if (result.data.updateParticipantProgramDataByProgramNumber.responseCode === "SUCCESS") {
        console.log("File uploaded successfully");
        setIsUploadModalOpen(false);
        setUploadFile(null);
        setUploadLabel("");
      } else {
        console.error("Upload failed:", result.data.updateParticipantProgramDataByProgramNumber.message);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const result = await updateParticipant({
        variables: {
          programNumber,
          input: {
            scans: [{ id, deleted: true }],
          },
        },
      });

      if (result.data.updateParticipantProgramDataByProgramNumber.responseCode === "SUCCESS") {
        console.log("File deleted successfully");
        setShowDeleteConfirm(null);
      } else {
        console.error("Deletion failed:", result.data.updateParticipantProgramDataByProgramNumber.message);
      }
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  const handleTransferScans = async (participantToTransfer: any) => {
    if (!selectedScanIds.length) return;
    const confirmed = window.confirm(
      `Are you sure you want to transfer these scans to ODR: ${participantToTransfer.id} CID: ${participantToTransfer.cid || ""} ?`
    );
    if (!confirmed) return;

    await transferScans({
      variables: {
        odrnumber: Number(participantToTransfer.id),
        scanID: selectedScanIds,
      },
    });
    setSelectedScanIds([]);
    setTransferScanModal(false);
  };

  const toggleSelectedScan = (scanId: string, checked: boolean) => {
    if (checked) {
      setSelectedScanIds((current) => Array.from(new Set([...current, scanId])));
      return;
    }
    setSelectedScanIds((current) => current.filter((id) => id !== scanId));
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const getAllScans = (): any[] => {
    const allScans: any[] = [];
    const types = [
      { type: "courtFile", label: "Court File" },
      { type: "rapSheet", label: "Rapsheet" },
      { type: "otherFile", label: "Other File" }
    ];
    
    types.forEach(({ type, label }) => {
      const typeScans = scans.filter((scan) => scan.filetype === type);
      if (typeScans.length > 0) {
        typeScans.forEach(scan => {
          allScans.push({ ...scan, typeLabel: label });
        });
      } else {
        allScans.push({ 
          id: `empty-${type}`, 
          filetype: type, 
          typeLabel: label, 
          isEmpty: true 
        });
      }
    });
    
    return allScans;
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open: boolean) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <DialogContent
          style={modalStyles.content}
          overlayStyle={modalStyles.overlay}
          className="max-w-none"
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
        <div className="w-full h-full bg-white rounded-lg shadow-lg flex flex-col">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <h2 className="text-xl font-semibold text-gray-900">Upload or delete scans for this person</h2>
            {canTransferScans && (
              <button
                id="participant-scans-transfer"
                disabled={selectedScanIds.length === 0}
                onClick={() => setTransferScanModal(true)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Transfer Selected
              </button>
            )}
          </div>

          {selectedScan && (
            <ScanPreview
              odrnumber={odrNumber}
              scan={selectedScan}
              close={() => setSelectedScan(null)}
            />
          )}

          <div className="flex-1 overflow-auto p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {canTransferScans && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Select
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File Name/Label
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Upload Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getAllScans().map((scan, index) => (
                    <tr key={scan.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {canTransferScans && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {!scan.isEmpty && (
                            <input
                              id={`participant-scans-select-${scan.id}`}
                              aria-label={`Select scan ${scan.filename || scan.filelabel || scan.id}`}
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300"
                              checked={selectedScanIds.includes(String(scan.id))}
                              onChange={(event) => toggleSelectedScan(String(scan.id), event.target.checked)}
                            />
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {scan.typeLabel}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {scan.isEmpty ? (
                          <span className="text-gray-400 italic">No file uploaded</span>
                        ) : (
                          <button
                            className="text-blue-600 hover:text-blue-800 font-medium"
                            onClick={() => setSelectedScan(scan.id)}
                          >
                            {scan.filetype === "otherFile" ? scan.filelabel : scan.filename || scan.typeLabel}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {scan.isEmpty ? (
                          <span className="text-gray-400">-</span>
                        ) : (
                          new Date(scan.filedate).toLocaleString()
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => {
                            setUploadType(scan.filetype);
                            setIsUploadModalOpen(true);
                          }}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          {scan.isEmpty ? 'Upload' : 'Replace'}
                        </button>
                        {!scan.isEmpty && (
                          <button
                            onClick={() => setShowDeleteConfirm(scan.id)}
                            className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isUploadModalOpen}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setIsUploadModalOpen(false);
          }
        }}
      >
        <DialogContent
          style={modalStyles.content}
          overlayStyle={modalStyles.overlay}
          className="max-w-none"
          onInteractOutside={(event) => event.preventDefault()}
        >
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg">
          <div className="p-6 space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">Upload File</h2>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type of file:
                </label>
                <select
                  value={uploadType}
                  onChange={(e) => setUploadType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select file type</option>
                  <option value="courtFile">Court file for case</option>
                  <option value="rapSheet">Rapsheet</option>
                  <option value="otherFile">Other File</option>
                </select>
              </div>

              {uploadType === "courtFile" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Insert code:
                  </label>
                  <input
                    type="text"
                    value={uploadLabel}
                    onChange={(e) => setUploadLabel(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {uploadType === "otherFile" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Insert label:
                  </label>
                  <input
                    type="text"
                    value={uploadLabel}
                    onChange={(e) => setUploadLabel(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              <div
                {...getRootProps()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 transition-colors"
              >
                <input {...getInputProps()} />
                <p className="text-sm text-gray-600">
                  {isDragActive
                    ? "Drop the file here ..."
                    : "Drag 'n' drop a file here, or click to select a file"}
                </p>
              </div>

              {uploadFile && (
                <p className="text-sm text-gray-600">
                  Selected file: {uploadFile.name}
                </p>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Upload
                </button>
                <button
                  type="button"
                  onClick={() => setIsUploadModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(showDeleteConfirm)}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setShowDeleteConfirm(null);
          }
        }}
      >
        <DialogContent
          style={modalStyles.content}
          overlayStyle={modalStyles.overlay}
          className="max-w-none"
          onInteractOutside={(event) => event.preventDefault()}
        >
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg">
          <div className="p-6 space-y-6">
            <h2 className="text-xl font-semibold text-gray-900">Confirm Delete</h2>
            <p className="text-gray-600">
              Are you sure you want to delete this file? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => showDeleteConfirm && handleDelete(showDeleteConfirm)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Delete
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={transferScanModal}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setTransferScanModal(false);
          }
        }}
      >
        <DialogContent
          style={modalStyles.content}
          overlayStyle={modalStyles.overlay}
          className="max-w-none"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <div className="w-full h-full bg-white rounded-lg shadow-lg flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900">Transfer Scans</h2>
              <button
                onClick={() => setTransferScanModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <SearchParticipants onSelect={handleTransferScans} />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditScans;
